import { formatDate } from "@plasma/utils/time";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  locationSalesGetVoidedItemsData,
  type VoidedItemEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type ExtendedVoidedItem = VoidedItemEntry;

interface VoidsViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const VoidsView = ({
  locationId,
  dateRange,
  tableId,
}: VoidsViewProps) => {
  const data =
    useQuery(locationSalesGetVoidedItemsData, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    }) ?? [];
  const isLoading = data === undefined;

  const columnHelper = createColumnHelper<ExtendedVoidedItem>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("posDevice", {
          header: "POS Device",
          cell: ({ getValue }) => getValue()?.name ?? "N/A",
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.posDevice
              ? filterValue.includes(original.posDevice.id)
              : false,
          meta: {
            getCellForExport: (d: ExtendedVoidedItem["posDevice"]) =>
              d?.name ?? "",
            faceting: {
              title: "POS Device",
              getOption: (e: ExtendedVoidedItem["posDevice"]) => ({
                value: e?.id ?? "",
                label: e?.name ?? "unnamed device",
              }),
            },
          },
        }),
        columnHelper.accessor("voidReason", {
          header: "Void Reason",
          cell: ({ getValue }) => getValue() ?? "N/A",
        }),
        columnHelper.accessor("employee", {
          header: "Employee Name",
          cell: ({ getValue }) => {
            const employee = getValue();
            if (!employee) {
              return "N/A";
            }
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}'s avatar`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.employee
              ? filterValue.includes(original.employee.id)
              : false,
          meta: {
            getCellForExport: (e: ExtendedVoidedItem["employee"]) =>
              e ? `${e.firstName} ${e.lastName}` : "N/A",
            faceting: {
              title: "Employee",
              getOption: (e: ExtendedVoidedItem["employee"]) => ({
                value: e?.id ?? "",
                label: e ? `${e.firstName} ${e.lastName}` : "N/A",
              }),
            },
          },
        }),
        columnHelper.accessor("orderId", {
          header: "Order ID",
        }),
        columnHelper.accessor("orderCreatedAt", {
          header: "Date of Trans.",
          cell: ({ getValue }) =>
            formatDate(new Date(getValue()), "MMM D, YYYY"),
          meta: {
            getCellForExport: (d: ExtendedVoidedItem["orderCreatedAt"]) =>
              new Date(d).toISOString(),
          },
        }),
        columnHelper.accessor("amountVoided", {
          header: "Voided Amount",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: ExtendedVoidedItem["amountVoided"]) => d,
          },
        }),
      ] as ColumnDef<ExtendedVoidedItem>[],
    [columnHelper]
  );

  if (isLoading) {
    return <LoadingPanel message="Loading void data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
